import { registry, type ArgSpec } from "../registry";
import { createLogger } from "./logger";
import type { SdkGateway } from "./gateway";
import type { EventLog, LogEntry } from "./eventLog";

const log = createLogger("requestRunner");

/**
 * Maps a form's values onto POSITIONAL SDK arguments, in descriptor order (amendment A1). Blank
 * fields (`undefined` or `""`) fall back to the descriptor's `default`. Trailing `undefined`
 * values are dropped so optional trailing parameters are omitted rather than passed explicitly —
 * the real SDK is positional, e.g. `getAttribute(contactId, attribute)`, not one merged object,
 * and a `json`-typed field's parsed value IS the positional argument, never re-wrapped.
 */
export function buildArgs(
  spec: readonly ArgSpec[],
  values: Record<string, unknown>,
): unknown[] {
  const out: unknown[] = spec.map((a) => {
    const raw = values[a.k];
    return raw === undefined || raw === "" ? a.default : raw;
  });
  while (out.length > 0 && out[out.length - 1] === undefined) out.pop();
  return out;
}

/** Builds the `error` entry input shared by every failure path below. */
function errorInput(
  moduleId: string,
  methodName: string,
  message: string,
  contactId: string | undefined,
  durationMs?: number,
): Parameters<EventLog["append"]>[0] {
  return {
    kind: "error",
    moduleId,
    name: methodName,
    ...(contactId && { contactId }),
    ...(durationMs !== undefined && { durationMs }),
    error: { message },
  };
}

/** Invokes SDK request methods and records every outcome uniformly in the unified stream. */
export class RequestRunner {
  constructor(
    private readonly gateway: SdkGateway,
    private readonly eventLog: EventLog,
  ) {}

  /**
   * Invokes one request. Never throws and never rejects — failures become `error` entries
   * carrying the message, and even a throwing event log degrades to a stand-in entry rather
   * than propagating (see {@link record}).
   */
  async invoke(
    moduleId: string,
    methodName: string,
    values: Record<string, unknown>,
  ): Promise<LogEntry> {
    const contactId =
      typeof values.contactId === "string" ? values.contactId : undefined;
    const mod = registry.find((m) => m.id === moduleId);
    const client = this.gateway.getClient(moduleId);
    const fn = client?.[methodName];

    // Availability is checked BEFORE the interceptor guard: an unknown method on an interceptor
    // module is not an interceptor method, and reporting it as one misdirects the diagnosis.
    const descriptor = mod?.requests.find((r) => r.name === methodName);
    if (!mod || !descriptor || !client || typeof fn !== "function") {
      return this.record(
        errorInput(
          moduleId,
          methodName,
          `${moduleId}.${methodName} is not available on this client`,
          contactId,
        ),
      );
    }

    if (mod.kind === "interceptor") {
      return this.record(
        errorInput(
          moduleId,
          methodName,
          `${moduleId}.${methodName} is an interceptor method: it takes a callback and is ` +
            "driven by InterceptorManager, not RequestRunner",
          contactId,
        ),
      );
    }

    return this.invokeReady(
      moduleId,
      methodName,
      contactId,
      client,
      fn,
      descriptor.args,
      values,
    );
  }

  /** Calls a resolved, ready-to-invoke SDK method with the receiver preserved (amendment A1). */
  private async invokeReady(
    moduleId: string,
    methodName: string,
    contactId: string | undefined,
    client: Record<string, unknown>,
    fn: unknown,
    argSpec: readonly ArgSpec[],
    values: Record<string, unknown>,
  ): Promise<LogEntry> {
    const started = performance.now();
    let result: unknown;
    try {
      // Invoke via .apply(client, ...) so the method's receiver is preserved — extracting a
      // method loses `this`, and real SDK methods rely on it (amendment A1, defect 1).
      result = await (fn as (...a: unknown[]) => unknown).apply(
        client,
        buildArgs(argSpec, values),
      );
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      log.warn(`${moduleId}.${methodName} failed`, err);
      return this.record({
        kind: "error",
        moduleId,
        name: methodName,
        durationMs: Math.round(performance.now() - started),
        ...(contactId && { contactId }),
        error: {
          message: err.message,
          name: err.name,
          ...(err.stack && { stack: err.stack }),
        },
      });
    }
    const durationMs = Math.round(performance.now() - started);
    log.debug(`${moduleId}.${methodName} ok`, { durationMs, contactId });
    // Appended OUTSIDE the try: a throw from the event log must never be caught by the handler
    // above and recorded as a failure of a request that actually succeeded.
    return this.record({
      kind: "request",
      moduleId,
      name: methodName,
      payload: result,
      durationMs,
      ...(contactId && { contactId }),
    });
  }

  /**
   * Appends to the event log, degrading to an unrecorded stand-in entry (`seq: -1`) if the log
   * itself throws. `invoke` promises never to reject; a broken log must not break that promise,
   * and must not turn a successful request into a reported failure.
   */
  private record(input: Parameters<EventLog["append"]>[0]): LogEntry {
    try {
      return this.eventLog.append(input);
    } catch (e) {
      log.error("event log append failed; entry not recorded", e);
      return {
        ...input,
        seq: -1,
        ts: new Date().toISOString(),
        t: performance.now(),
      };
    }
  }
}
